import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useUser} from "../../../context/UserContext";
import {TraceService} from "../../../service/TraceService";
import {TraceType} from "../../../model/user/general/Trace";

interface TimeLeft {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

const DEADLINE = new Date('2025-09-30T21:59:59Z');

function calculateTimeLeft(): TimeLeft {
    const diff = DEADLINE.getTime() - Date.now();

    if (diff <= 0) {
        return {days: 0, hours: 0, minutes: 0, seconds: 0};
    }

    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60)
    };
}

function formatTwoDigits(value: number): string {
    return value.toString().padStart(2, '0');
}

function CountdownSegment(props: { label: string; value: number }) {
    const {label, value} = props;

    return (
        <div className="d-flex flex-column align-items-center px-2">
            <span className="fw-bold text-white">{formatTwoDigits(value)}</span>
            <small className="text-white-50 text-uppercase">{label}</small>
        </div>
    );
}

export default function HeaderSaleBanner() {
    const navigate = useNavigate();
    const {user, isAuthenticated} = useUser();

    const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft());
    const [isExpired, setIsExpired] = useState<boolean>(() => DEADLINE.getTime() <= Date.now());

    useEffect(() => {
        if (isExpired) {
            return;
        }

        const interval = setInterval(() => {
            const diff = DEADLINE.getTime() - Date.now();
            if (diff <= 0) {
                setIsExpired(true);
                clearInterval(interval);
                return;
            }

            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(interval);
    }, [isExpired]);

    const deadlineLabel = useMemo(() => {
        return DEADLINE.toLocaleString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Europe/Warsaw'
        });
    }, []);

    if (isExpired) {
        return null;
    }

    const handleRedirect = () => {
        TraceService.addTrace(TraceType.BtnClick, 'Sales Banner Produkacja Header');
        const targetPath = isAuthenticated && user?.isParent ? '/platforma/prezentacja' : '/platforma';
        navigate(targetPath);
    };

    return (
        <div
            className="w-100"
            style={{
                background: 'linear-gradient(90deg, rgba(42, 51, 66, 0.95) 0%, rgba(42, 209, 103, 0.9) 100%)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
            }}
        >
            <div className="container py-2">
                <div
                    className="d-flex flex-column flex-lg-row align-items-center justify-content-center gap-3 text-center text-lg-start">
                    <span className="text-white fw-semibold">
                        Zapisy do Programu Edukacja 2.0 zamykają się {deadlineLabel}. Pozostało jeszcze:
                    </span>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <CountdownSegment label="dni" value={timeLeft.days}/>
                        <CountdownSegment label="godz" value={timeLeft.hours}/>
                        <CountdownSegment label="min" value={timeLeft.minutes}/>
                        <CountdownSegment label="sek" value={timeLeft.seconds}/>
                    </div>
                    <button
                        className="btn btn-light btn-sm px-4 text-nowrap shadow-sm"
                        onClick={handleRedirect}
                    >
                        Zobacz szczegóły
                    </button>
                </div>
            </div>
        </div>
    );
}
